import { Button, Card, Checkbox, Flex, Modal, Progress, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuestion } from '../../../api/Questions';
import { useTagResponses } from '../../../api/Responses';
import {
  QCPhase,
  ResponseFilterOptions,
  TESelectionType,
  ThemeEnginePanel,
} from '../../../constants';
import useParams from '../../../hooks/useParams';
import { Blue1, BluePrimary, TealProgress } from '../../../styles';
import { displayKeyType } from '../../../types';

const { Text } = Typography;
const { confirm } = Modal;

const StyledCard = styled(Card)`
  height: 100%;
  cursor: pointer;
  ${(props) => props.$displayed && `background: ${Blue1};`};
  ${(props) => props.$displayed && `border: 2px solid ${BluePrimary};`};

  .ant-card-body {
    padding: 6px 8px;
  }
`;

const StyledText = styled(Text)`
  font-size: 12px;
`;

const StyledPercentageText = styled(Text)`
  font-size: 12px;
  text-wrap: nowrap;
`;

function ThemeCard({
  panel,
  phase,
  theme,
  checked,
  showDescriptions,
  handleSelection,
  displayKey,
  setDisplayKey,
  selectedResponseIds = [],
  setSelectedResponseIds = () => {},
  setActiveFilter = () => {},
}) {
  const { codeFrameId, questionId, surveyId } = useParams();
  const [isHovered, setHovered] = useState(false);

  const { data: question, isLoading: questionLoading } = useQuestion({ questionId, surveyId });
  const { mutate: tagResponses } = useTagResponses();

  const responsePercentage = question?.data
    ? theme.response_ids.length / question.data.response_count
    : 0;

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    setDisplayKey({ type: null, key: null });
    const type =
      panel === ThemeEnginePanel.EXPLORE_THEMES
        ? TESelectionType.SUGGESTED_THEME
        : TESelectionType.THEME;
    handleSelection({ type, checked: e.target.checked, id: theme.id });
  };

  const handleCardClick = () => {
    const type =
      panel === ThemeEnginePanel.EXPLORE_THEMES
        ? TESelectionType.SUGGESTED_THEME
        : TESelectionType.THEME;
    setDisplayKey(
      displayKey.key === theme.id ? { type: null, key: null } : { type, key: theme.id },
    );
    handleSelection(null);
    if (displayKey.key !== theme.id) {
      handleSelection({ type, checked: true, id: theme.id });
    }
    setActiveFilter(ResponseFilterOptions.ALL);
  };

  const confirmRemove = () => {
    confirm({
      title: 'Are you sure you want to remove the theme from the selected responses?',
      content: 'This cannot be undone.',
      okText: 'Remove',
      okButtonProps: { danger: true, type: 'primary' },
      onOk: () => {
        tagResponses({
          codeFrameId,
          data: {
            response_ids: selectedResponseIds,
            theme_id: theme.id,
            code_frame_id: codeFrameId,
            is_theme: false,
          },
        });
        setSelectedResponseIds([]);
      },
    });
  };

  const cardAction = () => {
    if (isHovered && selectedResponseIds.length) {
      return (
        <Flex vertical gap={4}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              tagResponses({
                codeFrameId,
                data: {
                  response_ids: selectedResponseIds,
                  theme_id: theme.id,
                  code_frame_id: codeFrameId,
                  is_theme: true,
                },
              });
              setSelectedResponseIds([]);
            }}
          >
            Add to theme
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              confirmRemove();
            }}
          >
            Remove
          </Button>
        </Flex>
      );
    }
    return (
      <Flex gap={4}>
        <Progress
          type="circle"
          percent={Math.round(responsePercentage * 100)}
          size={20}
          strokeColor={TealProgress}
        />
        <StyledPercentageText type="secondary">
          {Math.round(responsePercentage * 100)}%
        </StyledPercentageText>
      </Flex>
    );
  };

  return (
    <StyledCard
      size="small"
      bordered={false}
      loading={questionLoading}
      onClick={handleCardClick}
      $displayed={displayKey.key === theme.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Flex justify="space-between" align={showDescriptions ? 'start' : 'center'} gap={8}>
        <Flex align="start" gap={8}>
          {phase !== QCPhase.COMPLETE && (
            <Checkbox checked={checked} onClick={handleCheckboxChange} />
          )}
          <Flex vertical gap={2}>
            {theme.name}
            {showDescriptions && <StyledText type="secondary">{theme.description}</StyledText>}
          </Flex>
        </Flex>
        {cardAction()}
      </Flex>
    </StyledCard>
  );
}

ThemeCard.propTypes = {
  panel: PropTypes.string.isRequired,
  phase: PropTypes.string.isRequired,
  theme: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    response_ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
  checked: PropTypes.bool.isRequired,
  showDescriptions: PropTypes.bool.isRequired,
  handleSelection: PropTypes.func.isRequired,
  displayKey: displayKeyType.isRequired,
  setDisplayKey: PropTypes.func.isRequired,
  selectedResponseIds: PropTypes.arrayOf(PropTypes.number),
  setSelectedResponseIds: PropTypes.func,
  setActiveFilter: PropTypes.func,
};

export default ThemeCard;
